import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Clock, Flame } from "lucide-react";
import { fetchRecipes } from "@/lib/api";
import HeroImage from "@/components/HeroImage";

const CATEGORY_META = {
  sides: {
    label: "Sides",
    number: "01",
    tagline: "Small plates, big opening moves",
    intro: "The first bite sets the mood — make it a good one.",
  },
  mains: {
    label: "Main Course",
    number: "02",
    tagline: "The centerpiece — the reason you sat down",
    intro: "These are the recipes worth clearing the calendar for.",
  },
  desserts: {
    label: "Desserts",
    number: "03",
    tagline: "The full-stop, but sweet",
    intro: "Nothing bad has ever happened over dessert.",
  },
};

export default function Category() {
  const { category } = useParams();
  const meta = CATEGORY_META[category] || { label: category, number: "", tagline: "", intro: "" };
  const [recipes, setRecipes] = useState([]);

  useEffect(() => {
    fetchRecipes(category).then(setRecipes).catch(() => setRecipes([]));
  }, [category]);

  return (
    <div className="min-h-screen bg-background text-foreground pt-32 pb-24" data-testid={`category-page-${category}`}>
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="grid md:grid-cols-12 gap-8 items-end mb-20 md:mb-32"
        >
          <div className="md:col-span-8">
            <p className="font-script text-accent text-3xl mb-3">course {meta.number}</p>
            <h1 className="font-display text-6xl md:text-8xl lg:text-9xl leading-[0.9] tracking-tighter" data-testid="category-title">
              {meta.label}
            </h1>
          </div>
          <div className="md:col-span-4">
            <p className="font-serif-alt italic text-xl text-foreground/70 border-l-2 border-accent pl-6 leading-relaxed">
              {meta.intro}
            </p>
          </div>
        </motion.div>

        {/* Recipe grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-16 gap-x-8">
          {recipes.map((r, i) => (
            <motion.div
              key={r.slug}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.1 }}
            >
              <RecipeCard recipe={r} />
            </motion.div>
          ))}
        </div>

        {recipes.length === 0 && (
          <p className="font-serif-alt italic text-foreground/50 text-center py-32">
            Loading recipes…
          </p>
        )}
      </div>
    </div>
  );
}

function RecipeCard({ recipe }) {
  return (
    <Link
      to={`/recipe/${recipe.slug}`}
      data-testid={`recipe-card-${recipe.slug}`}
      className="group block"
    >
      <div className="relative overflow-hidden aspect-[4/5] mb-5 bg-secondary rounded-sm">
        <HeroImage
          src={recipe.hero_image}
          title={recipe.title}
          className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms] ease-out"
          eyebrow={recipe.origin}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      </div>
      <div className="flex items-center gap-4 text-xs uppercase tracking-widest text-foreground/50 font-serif-alt mb-3">
        <span className="flex items-center gap-1.5"><Flame className="w-3.5 h-3.5" strokeWidth={1.5} />{recipe.difficulty}</span>
        <span className="w-1 h-1 rounded-full bg-foreground/30" />
        <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" strokeWidth={1.5} />{recipe.total_time}</span>
        <span className="w-1 h-1 rounded-full bg-foreground/30" />
        <span>{recipe.origin}</span>
      </div>
      <h3 className="font-display text-3xl md:text-4xl leading-tight tracking-tighter group-hover:text-accent transition-colors duration-300">
        {recipe.title}
      </h3>
      <p className="font-serif-alt italic text-lg text-foreground/70 mt-2 leading-snug">
        {recipe.subtitle}
      </p>
      <div className="mt-4 inline-flex items-center gap-2 font-serif-alt text-sm border-b border-foreground/30 pb-0.5 group-hover:border-accent group-hover:text-accent transition-all duration-300">
        Read the recipe
        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" strokeWidth={1.5} />
      </div>
    </Link>
  );
}
